import { HttpStatusCode } from "@angular/common/http";
import { from, MonoTypeOperatorFunction, Observable, ObservableInput, ObservedValueOf, of, OperatorFunction } from "rxjs";
import { catchError, map, withLatestFrom } from "rxjs/operators";
import { AppInjector } from "../../app-injector.module";
import { ApiException, ErrorDto } from "../../services/api.services";
import { SnackService } from 'src/app/services/snack.service';
import { isNullOrUndefined } from "./is-null-or-undefined";
import { AuthService } from 'src/app/auth/auth.service';
import { select } from '@ngrx/store';
import { logger } from '../../pbir/utils/logger';

const LOGGER = logger('AppError');

export interface AppErrorData {
  status: HttpStatusCode;
  message?: string;
  content?: any;
};

let _id = 0;

export class AppError extends Error {
  constructor(public readonly data: AppErrorData | ApiException, cause?: any) { 
    super(data.message, { cause: cause });  
  }

  id = _id++;

  status = this.data.status;
  message = this.data.message;

  preventSnack?: boolean = null;

  toErrorDto() {
    return <ErrorDto>{
      error: this.message
    };
  }

  showErrorSnack() {
    if (this.preventSnack)
      return;

    AppInjector.get(SnackService).openError(this.message);
  }

  getErrorContentAsJson() {
    if (!(this.data instanceof ApiException)) {
      if (this.data?.content && this.data.content instanceof Blob) {
        return from(this.data?.content.text()).pipe(
          map(text => JSON.parse(text))
        );
      }
    }

    return of(null);
  }
}

// This gives a compiler error when trying to pass AppError to createAppError
type NotAppError<T> = T extends AppError ? never : T;

export function createAppError<T>(error_: NotAppError<T>, options?: { preventSnack?: boolean }): AppError {
  let appError: AppError;

  let content = null;

  if (typeof error_ === 'object' && 'error' in error_) {
    content = (error_ as any).error;
  }

  if (error_ instanceof AppError) {
    appError = error_;
  }
  else {
    appError = createAppErrorInternal(error_, content);
  }

  if (isNullOrUndefined(appError.preventSnack) && !isNullOrUndefined(options?.preventSnack)) {
    appError.preventSnack = options?.preventSnack ?? false;
  }

  return appError;
}

function createAppErrorInternal<T>(error_: NotAppError<T>, content?: any): AppError {
  let error = <any>error_;

  if (!isNullOrUndefined(error)) {
    try {
      if (typeof error === 'string') {
        return new AppError({
          status: 400,
          message: error,
          content
        }, error_);
      }

      if (error instanceof Error) {
        return new AppError({
          status: 400,
          message: error.message,
          content
        }, error_);
      }

      if (error instanceof AppError) {
        LOGGER.warn('AppError passed to createAppError - returning original instance');
        return error;
      }

      if (error instanceof ApiException) {
        return new AppError(error, error_);
      }

      let status = HttpStatusCode.BadRequest
      let message = unwrapMessage(error);

      if ('status' in error) {
        return new AppError({
          status: error.status,
          message: message,
          content
        }, error_);
      }

      if (typeof error === 'string') {
        try {
          let deserializedError = JSON.parse(error);

          return createAppError(deserializedError);
        }
        catch {
          message = error;
        }
      }

      return new AppError({
        status,
        message,
        content
      }, error_);
    }
    catch (innerError) {
      LOGGER.error(innerError);
    }
  }

  return new AppError(<AppErrorData>{ message: 'An unknown error occurred.' }, error_);
}

function unwrapMessage(error: any) {
  let message = 'Unknown Error';

  if ('detailedMessage' in error && typeof error.detailedMessage === 'string') {
    message = error.detailedMessage;
  }
  else if ('message' in error && typeof error.message === 'string') {
    message = error.message;
  }
  else if ('description' in error && typeof error.description === 'string') {
    message = error.description;
  }
  else if ('error' in error && typeof error.error === 'string') {
    message = error.error;
  }

  return message;
}

export function catchAppError<T, O extends ObservableInput<AppError>>(
  options: { showErrorSnack?: boolean, errorMessage?: string | ((err: AppError) => string) }): OperatorFunction<T, T | ObservedValueOf<O> | any>;
export function catchAppError<T, O extends ObservableInput<AppError>>(
  selector?: ((err?: AppError, caught?: Observable<T>) => (O | undefined | void | any)) | any,
  options?: { showErrorSnack?: boolean, errorMessage?: string | ((err: AppError) => string) }): OperatorFunction<T, T | ObservedValueOf<O> | any>;
export function catchAppError<T, O extends ObservableInput<AppError>>(
  selector?: any,
  options?: any): OperatorFunction<T, T | ObservedValueOf<O> | any> {
  return catchError((error, caught) => {
    if (typeof selector == 'object') {
      options = selector;
      selector = null;
    }

    LOGGER.error(error);

    if (error && 'cause' in error)
      LOGGER.error(error.cause);

    let appError = createAppError(error);

    if (!isNullOrUndefined(options?.errorMessage)) {
      appError.message = typeof options.errorMessage === 'function'
        ? options.errorMessage(appError)
        : options.errorMessage;
    }

    if (options?.showErrorSnack ?? true) {
      appError?.showErrorSnack();
    }

    if (!selector)
      return of(error);

    let result = selector(appError, caught);
    if (result !== undefined)
      return <Observable<any>>(<unknown>result);

    return of(error);
  });
}
